import type { WorkflowDefinition } from "../types";

/**
 * W14 — Build executive and spokesperson visibility.
 *
 * Organization-scoped because it starts before the subject exists: step one
 * creates a PERSON property, and the run binds to it. `perTarget` on that
 * property, so a comms team setting up three spokespeople in the same week has
 * three runs rather than three people fighting over one checklist.
 *
 * A person is tracked the same way a brand is, with two relabellings: the
 * competitor set is a peer set, and brand consistency reads as identity
 * accuracy — how accurately AI describes this person, which is the number the
 * person themselves will ask about first.
 */
export const executiveVisibilityWorkflow: WorkflowDefinition = {
	id: "executive-visibility",
	wNumber: 14,
	title: "Build executive visibility in AI answers",
	outcome:
		"Check that AI describes your spokesperson accurately and names them beside their peers.",
	audiences: ["pr", "inhouse"],
	uses: [
		"Properties",
		"AI Reports",
		"Identity Accuracy",
		"Peers",
		"Prompts",
		"Projects",
	],
	scope: "organization",
	bindsProperty: true,
	bindSubjectType: "PERSON",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "property", label: "Person" },
	// Every organization can track a person; the subject the run creates is a
	// PERSON by construction, so there is nothing here to gate on.
	isApplicable: () => true,
	articleSlug: "how-to-build-executive-visibility-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "create-person",
			title: "Add the spokesperson",
			page: "Organization dashboard",
			route: "org:/",
			criterion: "You've added the person.",
			why: "A person is tracked in their own right, with their own questions and peers. An executive can be invisible while their company is everywhere.",
			docsHref: "/docs/ai-visibility-features/person-tracking",
			tasks: [
				"Add a property with the person as the subject",
				"Use the name people really call them, plus former names",
				"Point it at their bio page or profile",
			],
			complete: { kind: "bound", subjectType: "PERSON" },
			// Completion is this run binding to a person, not the organization
			// having people in it — the fourth spokesperson is not done because
			// the first three exist.
			signal: ({ run }) => ({
				kind: "checklist",
				items: [
					{
						label: "Person added",
						done: Boolean(run?.propertyId),
					},
				],
			}),
		},
		{
			id: "report",
			title: "Start an AI Visibility Report for them",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've completed one report.",
			why: "This is the baseline, and for a person it is usually uncomfortable. It shows which credentials survive and which belong to someone else.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start a report and wait 4 to 15 minutes",
				"Read the description back as the person would",
				"Note anything factually wrong for your first outreach list",
			],
			complete: { kind: "count", key: "completedReportCount", gte: 1 },
			spend: { kind: "aiReport" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed reports",
							value: state.property.completedReportCount,
							target: 1,
						}
					: null,
		},
		{
			id: "identity-review",
			title: "Check how accurately AI describes them",
			page: "Identity Accuracy",
			route: "/dashboard/consistency",
			criterion: "You've read the identity checks.",
			why: "For a person, accuracy comes before visibility. The checks show where AI gets the employer, the role or a credential wrong.",
			docsHref: "/docs/ai-visibility-features/person-tracking",
			tasks: [
				"Read the identity checks and mark what is actually wrong",
				"Trace each wrong claim back to its sources",
				"Start with the profiles the person controls",
			],
			complete: { kind: "manual" },
		},
		{
			id: "peers",
			title: "Name the peers they are compared against",
			page: "Peers",
			route: "/competitors",
			criterion: "You've added three peers.",
			why: "Share of voice for a person only means something against a named set. The right peers are other people, not rival companies.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Add the people who show up in the same answers",
				"Three to six is the useful range",
				"Add nicknames, initials and former names",
			],
			complete: { kind: "count", key: "competitorCount", gte: 3 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Peers tracked",
							value: state.property.competitorCount,
							target: 3,
						}
					: null,
		},
		{
			id: "prompts",
			title: "Build the prompts for their name and their subject",
			page: "Prompts",
			route: "/discovery-queries",
			criterion: "You've added five active prompts.",
			why: "Identity questions test accuracy. Expertise questions are where a spokesperson either gets listed or does not.",
			docsHref: "/docs/getting-started/choosing-prompts",
			tasks: [
				"Keep the identity prompts and fix any that misread them",
				"Add expertise questions that name nobody",
				"Add the comparisons their peers would appear in",
			],
			complete: { kind: "count", key: "activePromptCount", gte: 5 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active prompts",
							value: state.property.activePromptCount,
							target: 5,
						}
					: null,
		},
		{
			id: "project",
			title: "Put the prompts on daily tracking",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up an active project for this person.",
			why: "A one-off report is a photograph. Daily tracking shows a keynote or a byline as a real step change weeks later.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a project and add the person's prompts",
				"Set the window to the campaign or speaking season",
				"Note their appearances as they happen",
			],
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
	],
};
